import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, ListTodo, Building2 } from "lucide-react";
import { toast } from "sonner";

interface TeamTask {
  id: string;
  name: string;
  description: string | null;
  due_in_days: number;
  created_at: string;
}

interface OrgTask {
  id: string;
  name: string;
  description: string | null;
  due_in_days: number;
  is_enabled: boolean;
}

interface TeamTasksManagementProps {
  teamId: string;
}

export function TeamTasksManagement({ teamId }: TeamTasksManagementProps) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState<TeamTask[]>([]);
  const [orgTasks, setOrgTasks] = useState<OrgTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TeamTask | null>(null);
  const [saving, setSaving] = useState(false);
  const [togglingTaskId, setTogglingTaskId] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    due_in_days: 7
  });

  useEffect(() => {
    fetchTasks();
    fetchOrgTasks();
  }, [teamId]);

  const fetchTasks = async () => {
    try {
      const { data, error } = await supabase
        .from('default_team_tasks')
        .select('id, name, description, due_in_days, created_at')
        .eq('team_id', teamId)
        .order('name');

      if (error) throw error;
      setTasks((data || []).map(d => ({ ...d, due_in_days: d.due_in_days || 7 })));
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Fehler beim Laden der Aufgaben');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrgTasks = async () => {
    if (!user) return;
    
    try {
      // First get user's organization
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.organization_id) return;

      // Get all organization tasks
      const { data: allOrgTasks, error: orgError } = await supabase
        .from('default_organization_tasks')
        .select('id, name, description, due_in_days')
        .eq('organization_id', profile.organization_id)
        .order('name');

      if (orgError) throw orgError;

      // Get team's settings for these tasks
      const { data: settings, error: settingsError } = await supabase
        .from('team_organization_task_settings')
        .select('organization_task_id, is_enabled')
        .eq('team_id', teamId);

      if (settingsError) throw settingsError;

      // Create a map of settings
      const settingsMap = new Map(
        (settings || []).map(s => [s.organization_task_id, s.is_enabled])
      );

      // Combine tasks with their enabled state (default to true if no setting exists)
      const tasksWithSettings: OrgTask[] = (allOrgTasks || []).map(task => ({
        id: task.id,
        name: task.name,
        description: task.description,
        due_in_days: task.due_in_days,
        is_enabled: settingsMap.has(task.id) ? settingsMap.get(task.id)! : true
      }));

      setOrgTasks(tasksWithSettings);
    } catch (error) {
      console.error('Error fetching org tasks:', error);
    }
  };

  const toggleOrgTask = async (taskId: string, enabled: boolean) => {
    setTogglingTaskId(taskId);
    try {
      // Upsert the setting
      const { error } = await supabase
        .from('team_organization_task_settings')
        .upsert({
          team_id: teamId,
          organization_task_id: taskId,
          is_enabled: enabled
        }, {
          onConflict: 'team_id,organization_task_id'
        });

      if (error) throw error;

      // Update local state
      setOrgTasks(prev => prev.map(task => 
        task.id === taskId ? { ...task, is_enabled: enabled } : task
      ));

      toast.success(enabled ? 'Aufgabe aktiviert' : 'Aufgabe deaktiviert');
    } catch (error) {
      console.error('Error toggling org task:', error);
      toast.error('Fehler beim Ändern der Einstellung');
    } finally {
      setTogglingTaskId(null);
    }
  };

  const openNewDialog = () => {
    setEditingTask(null);
    setFormData({ name: "", description: "", due_in_days: 7 });
    setDialogOpen(true);
  };

  const openEditDialog = (task: TeamTask) => {
    setEditingTask(task);
    setFormData({
      name: task.name,
      description: task.description || "",
      due_in_days: task.due_in_days
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Bitte geben Sie einen Namen ein');
      return;
    }

    setSaving(true);
    try {
      if (editingTask) {
        // Update existing task
        const { error } = await supabase
          .from('default_team_tasks')
          .update({
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            due_in_days: formData.due_in_days
          })
          .eq('id', editingTask.id);

        if (error) throw error;
        toast.success('Aufgabe aktualisiert');
      } else {
        // Create new task
        const { error } = await supabase
          .from('default_team_tasks')
          .insert({
            team_id: teamId,
            name: formData.name.trim(),
            description: formData.description.trim() || null,
            due_in_days: formData.due_in_days
          });

        if (error) throw error;
        toast.success('Aufgabe erstellt');
      }

      setDialogOpen(false);
      fetchTasks();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (taskId: string) => {
    if (!confirm('Möchten Sie diese Aufgabe wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('default_team_tasks')
        .delete()
        .eq('id', taskId);

      if (error) throw error;
      toast.success('Aufgabe gelöscht');
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Fehler beim Löschen');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Team Tasks Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Team-Aufgaben</CardTitle>
            <CardDescription>
              Definieren Sie wiederkehrende Aufgaben für Ihr Team
            </CardDescription>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNewDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Neue Aufgabe
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {editingTask ? 'Aufgabe bearbeiten' : 'Neue Aufgabe erstellen'}
                </DialogTitle>
                <DialogDescription>
                  Erstellen Sie eine wiederkehrende Aufgabe für Ihr Team.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="z.B. Wochenbericht erstellen"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Beschreibung (optional)</Label>
                  <Textarea
                    id="description"
                    placeholder="Beschreiben Sie die Aufgabe..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="due_in_days">Fällig in (Tagen)</Label>
                  <Input
                    id="due_in_days"
                    type="number"
                    min={1}
                    max={365}
                    value={formData.due_in_days}
                    onChange={(e) => setFormData({ ...formData, due_in_days: parseInt(e.target.value) || 7 })}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Abbrechen
                </Button>
                <Button onClick={handleSave} disabled={saving}>
                  {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {editingTask ? 'Speichern' : 'Erstellen'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-12">
              <ListTodo className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">Keine Aufgaben vorhanden</h3>
              <p className="text-muted-foreground mb-4">
                Erstellen Sie Ihre erste Team-Aufgabe
              </p>
              <Button onClick={openNewDialog}>
                <Plus className="h-4 w-4 mr-2" />
                Erste Aufgabe erstellen
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex-1">
                    <h4 className="font-medium">{task.name}</h4>
                    {task.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {task.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Fällig in {task.due_in_days} {task.due_in_days === 1 ? 'Tag' : 'Tagen'}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="icon" onClick={() => openEditDialog(task)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(task.id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Organization Tasks Settings Card */}
      {orgTasks.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Organisations-Aufgaben</CardTitle>
                <CardDescription>
                  Aktivieren oder deaktivieren Sie Organisations-Aufgaben für Ihr Team
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {orgTasks.map((task) => (
                <div
                  key={task.id}
                  className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                    task.is_enabled ? 'bg-card' : 'bg-muted/50 opacity-60'
                  }`}
                >
                  <div className="flex-1">
                    <h4 className={`font-medium ${!task.is_enabled && 'text-muted-foreground'}`}>
                      {task.name}
                    </h4>
                    {task.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {task.description}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-2">
                      Fällig in {task.due_in_days} {task.due_in_days === 1 ? 'Tag' : 'Tagen'}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">
                      {task.is_enabled ? 'Aktiv' : 'Inaktiv'}
                    </span>
                    <Switch
                      checked={task.is_enabled}
                      onCheckedChange={(checked) => toggleOrgTask(task.id, checked)}
                      disabled={togglingTaskId === task.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
